import * as React from 'react';
import { useSelector } from 'react-redux';
import DevBoxes from '../../components/dev-box/dev-boxes';
import { DevBoxViewModel } from '../../components/dev-box/models';
import { getDevBoxViewModels, getDevBoxViewModelsInProject } from '../../components/dev-box/selectors';
import Environments from '../../components/environment/environments';
import { EnvironmentViewModel } from '../../components/environment/models';
import { getEnvironmentViewModels, getEnvironmentViewModelsInProject } from '../../components/environment/selectors';
import NoResourcesFilterContainer from '../../components/no-resources-filter';
import { ResourceFilterType } from '../../constants/filters';
import { useProjectFilterContext, useResourceFilterContext } from '../../hooks/context/filters';
import HomeLoadingState, { HomeResourcePlaceholders } from './home-loading-state';

interface ResourcesProps {
    hasDevBoxes: boolean;
    hasEnvironments: boolean;
    isResourceDiscoveryInProgress: boolean;
}

interface ResourcesComponentProps {
    hasDevBoxes: boolean;
    hasEnvironments: boolean;
    devBoxes: DevBoxViewModel[];
    environments: EnvironmentViewModel[];
    isResourceDiscoveryInProgress: boolean;
}

export const getVisibleDevBoxes = (
    hasDevBoxes: boolean,
    devBoxes: DevBoxViewModel[],
    resourceFilter: ResourceFilterType | undefined,
    projectFilter: string | undefined
): DevBoxViewModel[] => {
    const filtersIncludeDevBoxes = resourceFilter === undefined || resourceFilter === ResourceFilterType.DevBoxes;

    if (!hasDevBoxes || !filtersIncludeDevBoxes) {
        return [];
    }

    return projectFilter ? getDevBoxViewModelsInProject(devBoxes, projectFilter) : devBoxes;
};

export const getVisibleEnvironments = (
    hasEnvironments: boolean,
    environments: EnvironmentViewModel[],
    resourceFilter: ResourceFilterType | undefined,
    projectFilter: string | undefined
): EnvironmentViewModel[] => {
    const filtersIncludeEnvironments =
        resourceFilter === undefined || resourceFilter === ResourceFilterType.DeploymentEnvironments;

    if (!hasEnvironments || !filtersIncludeEnvironments) {
        return [];
    }

    return projectFilter ? getEnvironmentViewModelsInProject(environments, projectFilter) : environments;
};

export const ResourcesComponent: React.FC<ResourcesComponentProps> = (props: ResourcesComponentProps) => {
    const { hasDevBoxes, hasEnvironments, devBoxes, environments, isResourceDiscoveryInProgress } = props;

    const { value: resourceFilter } = useResourceFilterContext();
    const { value: projectFilter } = useProjectFilterContext();

    const filteredDevBoxes = React.useMemo(
        () => getVisibleDevBoxes(hasDevBoxes, devBoxes, resourceFilter, projectFilter),
        [devBoxes, hasDevBoxes, projectFilter, resourceFilter]
    );
    const filteredEnvironments = React.useMemo(
        () => getVisibleEnvironments(hasEnvironments, environments, resourceFilter, projectFilter),
        [environments, hasEnvironments, projectFilter, resourceFilter]
    );

    const showDevBoxes = filteredDevBoxes.length !== 0;
    const showEnvironments = filteredEnvironments.length !== 0;

    if (!showDevBoxes && !showEnvironments) {
        return isResourceDiscoveryInProgress ? <HomeLoadingState /> : <NoResourcesFilterContainer />;
    }

    return (
        <>
            {showDevBoxes && <DevBoxes />}
            {showEnvironments && <Environments />}
            {isResourceDiscoveryInProgress && <HomeResourcePlaceholders count={1} />}
        </>
    );
};

export const Resources: React.FC<ResourcesProps> = (props: ResourcesProps) => {
    const { hasDevBoxes, hasEnvironments, isResourceDiscoveryInProgress } = props;

    const devBoxes = useSelector(getDevBoxViewModels);
    const environments = useSelector(getEnvironmentViewModels);

    return (
        <ResourcesComponent
            hasDevBoxes={hasDevBoxes}
            hasEnvironments={hasEnvironments}
            devBoxes={devBoxes}
            environments={environments}
            isResourceDiscoveryInProgress={isResourceDiscoveryInProgress}
        />
    );
};
